import React, { useRef, useState } from "react";
import Box from "@amzn/meridian/esm/components/box";
import Button from "@amzn/meridian/esm/components/button";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Icon from "@amzn/meridian/esm/components/icon";
import Popover from "@amzn/meridian/esm/components/popover";
import PopoverHeader from "@amzn/meridian/esm/components/popover/popover-header";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme/theme";
import userIcon from "@amzn/meridian-tokens/base/icon/user";

import { truncateAttendees } from "../../utils/summaryUtils";
import { THEME_COLOR } from "../../styles/colors";

/**
 * AttendeeListInDetailPage — shows truncated attendee names with an "All attendees" button.
 *
 * For default-UI users with structured attendee data:
 *   - "All attendees" navigates to the MeetingParticipants view (via onViewParticipants callback)
 *
 * For legacy users or when structured data is unavailable:
 *   - "All attendees" opens a Popover with simple name list (existing behavior)
 *
 * Props:
 * - attendeeNameList: comma-separated string of attendee names (always required)
 * - attendees: (optional) structured attendee array from backend
 * - isAttendanceReportEnabled: (optional) boolean — whether meeting attendance report feature is enabled
 * - onViewParticipants: (optional) callback to navigate to MeetingParticipants view
 */
const AttendeeListInDetailPage = ({ attendeeNameList, attendees, isAttendanceReportEnabled, onViewParticipants }) => {
  // Attendee list Popover (used only for legacy path)
  const buttonRef = useRef();
  const [open, setOpen] = useState(false);
  const onClickButton = () => setOpen(true);
  const onClose = () => setOpen(false);

  // Determine if we should use the enhanced participants view
  const useEnhancedView = isAttendanceReportEnabled && attendees && attendees.length > 0 && onViewParticipants;

  // If structured attendees data is available, derive display names from it (more complete than attendeeNameList)
  const displayNames = attendees && attendees.length > 0
    ? attendees.map(a => a.displayName || a.email || "").filter(Boolean)
    : attendeeNameList.split(",").map(n => n.trim()).filter(Boolean);

  const handleAllAttendeesClick = () => {
    if (useEnhancedView) {
      onViewParticipants();
    } else {
      onClickButton();
    }
  };

  return (
    <Row spacing="none" spacingInset="none" widths={["80%","20%"]}>
      <Row spacing="none" spacingInset="none">
        <Icon tokens={userIcon} color="secondary"/>
        <Row width="4px" />
        <Box>
          <Text type="b100" color="secondary">
            {truncateAttendees(displayNames)}
          </Text>
        </Box>
      </Row>
    
      <Button
        onClick={handleAllAttendeesClick}
        ref={buttonRef}
        type="link"
        size="small"
        aria-haspopup={useEnhancedView ? undefined : "dialog"}
        aria-expanded={useEnhancedView ? undefined : "false"}
      >
        <Theme tokens={{ textColorPrimary: THEME_COLOR.TEAL_GREEN }}>
          <Text type="b100">
            All attendees
          </Text>
        </Theme>
      </Button>

      {/* Popover — only rendered for legacy fallback */}
      {!useEnhancedView && (
        <Theme tokens={{ popoverHeaderFontSize: "14px", popoverHeaderLineHeight: "16px" }}>
          <Popover anchorNode={buttonRef.current} open={open} onClose={onClose} position="bottom" alignment="end" spacingInset="400">
            <PopoverHeader closeLabel="Close">All attendees</PopoverHeader>

            {/*Attendee name list*/}
            <Box width="186px" spacingInset="none" spacing="none">
              {displayNames.map((name, index) => (
                  <Theme key={index} tokens={{ textColorPrimary: THEME_COLOR.CHARCOAL_GRAY }}>
                    <Text type="b100" color="primary" alignment="left">
                      {name}
                    </Text>
                  </Theme>
                ))}
            </Box>
          </Popover>
        </Theme>
      )}
    </Row>
  );
};

export default AttendeeListInDetailPage;
